import React from 'react';
import { ContactWithDetails } from '../../../crm/components/contacts/types';
import { PropertyWithDetails } from '../../../crm/components/properties/types';
import { useResidentCategories } from './hooks/useResidentCategories';
import { 
  ListItemSection,
  formatCategoryName,
  formatPhoneNumber,
  formatEmail,
  formatAddress,
  ListItemDisplayData
} from '../../../components/common';
import { LoadingBar } from '../../../components/common/LoadingBar';
import '../../../components/common/CRMList.css';

interface ResidentContactListProps {
  contacts: ContactWithDetails[];
  error: string | null;
  selectedContactId?: number;
  onSelectContact: (contact: ContactWithDetails) => void;
  onDeleteContact: (contact: ContactWithDetails) => void;
  onCreateNew: () => void;
  globalLoading?: boolean;
  loadingMessage?: string;
  property?: PropertyWithDetails | null;
}

const ResidentContactList: React.FC<ResidentContactListProps> = ({
  contacts,
  error,
  selectedContactId,
  onSelectContact,
  onDeleteContact,
  onCreateNew,
  globalLoading = false,
  loadingMessage = 'Loading...',
  property
}) => {
  const { categories, loading: categoriesLoading } = useResidentCategories();
  
  // Helper function to get category name with fallback
  const getCategoryName = (typeId: number): string => {
    let categoryName = formatCategoryName(typeId, categories);
    
    // Fallback: if category not found, try direct lookup
    if (categoryName === 'Unknown Category' && typeId) {
      const foundCategory = categories.find(cat => {
        const catId = typeof cat.id === 'string' ? Number(cat.id) : cat.id;
        const contactTypeId = typeof typeId === 'string' ? Number(typeId) : typeId;
        return catId === contactTypeId;
      });
      
      if (foundCategory) {
        categoryName = foundCategory.name;
      } else if (categoriesLoading) {
        // Categories are still loading
        categoryName = 'Loading...';
      } else if (categories.length > 0) {
        // Log for debugging if category still not found but categories are loaded
        console.warn(`Category not found for typeId: ${typeId} (type: ${typeof typeId}). Available categories:`, categories.map(c => ({ id: c.id, name: c.name, idType: typeof c.id })));
      }
    }
    
    return categoryName;
  };

  const getDisplayData = (contact: ContactWithDetails): ListItemDisplayData => {
    const categoryName = getCategoryName(contact.typeId);
    const isTenant = categoryName === 'Tenant';
    
    // For Tenant contacts, use property address if available, otherwise fall back to contact address
    let addressValue = 'No address';
    if (isTenant && property?.address) {
      addressValue = formatAddress(property.address);
    } else if (contact.primaryAddress) {
      addressValue = formatAddress(contact.primaryAddress);
    }

    return {
      title: (contact.firstName || contact.lastName) 
        ? `${contact.firstName} ${contact.lastName}`.trim()
        : contact.businessName || 'Unnamed Contact',
      category: categoryName,
      fields: [
        ...(contact.businessName && (contact.firstName || contact.lastName) ? [{
          name: 'business',
          value: contact.businessName
        }] : []),
        {
          name: 'phone',
          value: formatPhoneNumber(contact.primaryPhone)
        },
        {
          name: 'email',
          value: formatEmail(contact.primaryEmail)
        },
        {
          name: 'address',
          value: addressValue
        }
      ]
    };
  };

  // Check if contact can be deleted (not Tenant)
  const canDeleteContact = (contact: ContactWithDetails): boolean => {
    const categoryName = getCategoryName(contact.typeId);
    return categoryName !== 'Tenant';
  };

  // Check if all Emergency contact types are already used
  const hasAvailableContactTypes = (): boolean => {
    if (categoriesLoading || categories.length === 0) {
      return true; // Don't disable while loading or if categories aren't loaded yet
    }

    const emergencyNames = ['Emergency 1', 'Emergency 2', 'Emergency 3'];
    const emergencyCategoryIds = new Set<number>();
    
    // Get all Emergency category IDs
    categories.forEach(cat => {
      const catId = typeof cat.id === 'string' ? Number(cat.id) : cat.id;
      if (emergencyNames.includes(cat.name)) {
        emergencyCategoryIds.add(catId);
      }
    });

    // If we don't have all three Emergency categories, allow adding (shouldn't happen, but safe fallback)
    if (emergencyCategoryIds.size < 3) {
      return true;
    }

    // Check which Emergency types are already used
    const usedEmergencyTypeIds = new Set<number>();
    contacts.forEach(contact => {
      if (contact.typeId) {
        const contactTypeId = typeof contact.typeId === 'string' ? Number(contact.typeId) : contact.typeId;
        if (emergencyCategoryIds.has(contactTypeId)) {
          usedEmergencyTypeIds.add(contactTypeId);
        }
      }
    });

    // If all three Emergency types are used, disable the button
    return usedEmergencyTypeIds.size < 3;
  };

  const canAddContact = hasAvailableContactTypes();

  if (error) {
    return <div className="app-error-message">{error}</div>;
  }

  return (
    <>
      <div className="list">
        <div className="list-header">
          <h3>Contacts ({contacts.length})</h3>
          <button
            className="app-btn app-btn-primary list-btn-add-item"
            onClick={canAddContact ? onCreateNew : undefined}
            disabled={!canAddContact}
            title={canAddContact ? "Add New Contact" : "All Emergency contact types are already in use"}
          >
            +
          </button>
        </div>

        <ListItemSection
          items={contacts}
          selectedItemId={selectedContactId}
          onSelectItem={onSelectContact}
          onDeleteItem={onDeleteContact}
          canDelete={canDeleteContact}
          deleteConfirmMessage={(contact) => 
            `Are you sure you want to delete ${contact.firstName} ${contact.lastName}? This action cannot be undone.`
          }
          loading={false}
          loadingMessage=""
          emptyMessage="No contacts available."
          getDisplayData={getDisplayData}
        />
      </div>
      
      <LoadingBar 
        isLoading={globalLoading} 
        message={loadingMessage}
      />
    </>
  );
};

export default ResidentContactList;

